import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Button, ProgressBar } from 'react-bootstrap';
import { FaBook, FaCheckCircle, FaClock, FaUserGraduate } from 'react-icons/fa';
import api from '../services/api';

const Dashboard = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [stats, setStats] = useState({
        enrolled_courses: 0,
        completed_lessons: 0,
        total_lessons: 0,
        progress_percentage: 0,
        recent_courses: [],
        in_progress_courses: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const userResponse = await api.get('/users/me/');
            setUser(userResponse.data);

            const isStudent = userResponse.data.role === 'student';
            const isInstructor = userResponse.data.role === 'instructor';

            if (isStudent) {
                const coursesResponse = await api.get('/courses/my_courses/');
                const enrolledCourses = coursesResponse.data || [];

                const coursesWithProgress = await Promise.all(
                    enrolledCourses.map(async (course) => {
                        try {
                            const progressRes = await api.get(`/courses/${course.id}/progress/`);
                            return {
                                ...course,
                                progress: {
                                    total: progressRes.data.total_lessons,
                                    completed: progressRes.data.completed_lessons,
                                    percentage: progressRes.data.percentage
                                }
                            };
                        } catch (err) {
                            return {
                                ...course,
                                progress: { total: 0, completed: 0, percentage: 0 }
                            };
                        }
                    })
                );

                const inProgressCourses = coursesWithProgress.filter(c =>
                    c.progress.total > 0 &&
                    c.progress.completed > 0 &&
                    c.progress.completed < c.progress.total
                );

                const totalLessons = coursesWithProgress.reduce((sum, c) => sum + c.progress.total, 0);
                const completedLessons = coursesWithProgress.reduce((sum, c) => sum + c.progress.completed, 0);

                setStats({
                    enrolled_courses: enrolledCourses.length,
                    completed_lessons: completedLessons,
                    total_lessons: totalLessons,
                    progress_percentage: totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0,
                    recent_courses: coursesWithProgress.slice(0, 3),
                    in_progress_courses: inProgressCourses.slice(0, 3),
                    instructor_stats: null,
                });

            } else if (isInstructor) {

                const statsResponse = await api.get('/courses/instructor_stats/');
                const data = statsResponse.data;

                setStats({
                    enrolled_courses: data.courses_count,
                    completed_lessons: data.published_count || 0,
                    total_lessons: data.students_count,
                    progress_percentage: data.courses_count > 0 ? Math.round((data.published_count / data.courses_count) * 100) : 0,
                    recent_courses: data.courses?.slice(0, 3) || [],
                    in_progress_courses: data.courses?.filter(c => !c.is_published) || [],
                    instructor_stats: data
                });
            }

        } catch (err) {
            console.error('❌ Błąd:', err);
            setError('Nie udało się pobrać danych');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Ładowanie dashboardu...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    const isStudent = user?.role === 'student';
    const isInstructor = user?.role === 'instructor';

    return (
        <Container className="mt-4">
            {/* Powitanie */}
            <div className="mb-4">
                <h1 className="display-6">
                    👋 Witaj, {user?.first_name || user?.username}!
                </h1>
                <p className="text-muted">
                    {isStudent && 'Kontynuuj naukę i rozwijaj swoje umiejętności'}
                    {isInstructor && 'Zarządzaj swoimi kursami i studentami'}
                </p>
            </div>

            {/* Statystyki */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="shadow-sm border-0">
                        <Card.Body className="text-center">
                            <FaBook size={30} className="text-primary mb-2" />
                            <h3>{stats.enrolled_courses}</h3>
                            <p className="text-muted small">Zapisanych kursów</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0">
                        <Card.Body className="text-center">
                            <FaCheckCircle size={30} className="text-success mb-2" />
                            <h3>{stats.completed_lessons}</h3>
                            <p className="text-muted small">Ukończonych lekcji</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0">
                        <Card.Body className="text-center">
                            <FaClock size={30} className="text-warning mb-2" />
                            <h3>{stats.total_lessons}</h3>
                            <p className="text-muted small">Łącznie lekcji</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0">
                        <Card.Body className="text-center">
                            <FaUserGraduate size={30} className="text-info mb-2" />
                            <h3>{stats.progress_percentage}%</h3>
                            <p className="text-muted small">Postęp</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Postęp ogólny */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Body>
                    <h5>📊 Ogólny postęp</h5>
                    <ProgressBar
                        now={stats.progress_percentage}
                        label={`${stats.progress_percentage}%`}
                        variant="primary"
                        style={{ height: '20px' }}
                    />
                    <div className="mt-2 d-flex justify-content-between">
                        <small className="text-muted">{stats.completed_lessons} ukończonych</small>
                        <small className="text-muted">{stats.total_lessons} łącznie</small>
                    </div>
                </Card.Body>
            </Card>

            {/* Dla studenta - ostatnie kursy */}
            {isStudent && (
                <Row>
                    <Col md={6}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <h5>📚 Ostatnie kursy</h5>
                                {stats.recent_courses.length === 0 ? (
                                    <p className="text-muted small">Nie masz jeszcze zapisanych kursów</p>
                                ) : (
                                    stats.recent_courses.map(course => (
                                        <div key={course.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                                            <div>
                                                <span className="fw-medium">{course.title}</span>
                                                <p className="text-muted small mb-0">{course.instructor_name}</p>
                                            </div>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => navigate(`/courses/${course.id}`)}
                                            >
                                                Otwórz →
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <h5>🔄 W trakcie</h5>
                                {stats.in_progress_courses.length === 0 ? (
                                    <p className="text-muted small">Nie masz kursów w trakcie</p>
                                ) : (
                                    stats.in_progress_courses.map(course => (
                                        <div key={course.id} className="border-bottom py-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <span className="fw-medium">{course.title}</span>
                                                    <p className="text-muted small mb-0">
                                                        {course.instructor_name}
                                                    </p>
                                                </div>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => navigate(`/courses/${course.id}`)}
                                                >
                                                    Kontynuuj →
                                                </Button>
                                            </div>
                                            <div className="mt-2">
                                                <div className="d-flex justify-content-between">
                                                    <small className="text-muted">Postęp</small>
                                                    <small className="text-muted fw-bold">
                                                        {Math.round(course.progress.percentage)}%
                                                    </small>
                                                </div>
                                                <ProgressBar
                                                    now={course.progress.percentage}
                                                    variant="primary"
                                                    style={{ height: '8px', borderRadius: '4px' }}
                                                />
                                                <small className="text-muted">
                                                    {course.progress.completed} / {course.progress.total} lekcji
                                                </small>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Dla instruktora - szybkie linki */}
            {isInstructor && (
                <>
                    <Row className="mb-4">
                        <Col md={4}>
                            <Card className="shadow-sm border-0">
                                <Card.Body className="text-center">
                                    <h1 className="text-primary">{stats.enrolled_courses}</h1>
                                    <p className="text-muted small">Twoje kursy</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="shadow-sm border-0">
                                <Card.Body className="text-center">
                                    <h1 className="text-success">{stats.total_lessons}</h1>
                                    <p className="text-muted small">Studentów</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="shadow-sm border-0">
                                <Card.Body className="text-center">
                                    <h1 className="text-info">{stats.completed_lessons}</h1>
                                    <p className="text-muted small">Opublikowanych kursów</p>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Lista kursów instruktora */}
                    <Card className="shadow-sm border-0">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5>📚 Twoje kursy</h5>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => navigate('/courses/create')}
                                >
                                    + Dodaj kurs
                                </Button>
                            </div>
                            {stats.recent_courses.length === 0 ? (
                                <p className="text-muted small">Nie masz jeszcze żadnych kursów</p>
                            ) : (
                                stats.recent_courses.map(course => (
                                    <div key={course.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                                        <div>
                                            <span className="fw-medium">{course.title}</span>
                                            <div className="text-muted small">
                                                {course.student_count || 0} studentów •
                                                {course.lessons_count || 0} lekcji •
                                                {course.is_published ? ' ✅ Opublikowany' : ' 📝 Szkic'}
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => navigate(`/courses/${course.id}`)}
                                        >
                                            Otwórz →
                                        </Button>
                                    </div>
                                ))
                            )}
                        </Card.Body>
                    </Card>
                </>
            )}
        </Container>
    );
};

export default Dashboard;